import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { opponent, type Replay, type Side } from '../../engine';
import { t } from '../../i18n';
import { addReplay, downloadJson } from '../../app/saves';
import {
  fetchRoom,
  fetchRoomReplay,
  identityNotSaved,
  loadIdentity,
  roomLink,
  saveName,
  type OnlineIdentity,
} from '../../app/online';
import { RemoteHost } from '../../app/remoteHost';
import type { Settings } from '../../app/settings';
import { ABANDON_MS, type RoomInfo } from '../../online/protocol';
import { Field } from '../components/Form';
import { ShareLink } from '../components/ShareLink';
import { MatchScreen } from './MatchScreen';
import { nameErrorText } from './OnlineScreen';
import { ReplayViewer } from './ReplayViewer';

type Stage =
  | { readonly kind: 'loading' }
  | { readonly kind: 'missing' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'replay'; readonly replay: Replay; readonly room: RoomInfo }
  /** `refused`: the room did not take the name the player had (not allowed, or another's). */
  | { readonly kind: 'name'; readonly refused?: NameRefusal }
  /** `ended`: the game was over when the room was opened. */
  | { readonly kind: 'live'; readonly ended?: boolean };

/**
 * How long a game that was over when opened waits for its room to come alive before showing its
 * replay instead (fetched over HTTP: the live connection may never come, with WebSockets blocked).
 */
export const LIVE_WAIT_MS = 5000;

/** Why the room did not take a name (src/online/room.ts hello). */
type NameRefusal = 'bad-name' | 'name-taken';

const seatNames = (room: RoomInfo | null): Record<Side, string> => ({
  S: room?.seats.S?.name ?? 'South',
  N: room?.seats.N?.name ?? 'North',
});

/**
 * A room from a link: join or watch its game, or watch the replay of a finished or shared
 * match. The link is also how a player gets back to a game after a reload or on another tab.
 */
export function RoomScreen({
  roomId,
  settings,
  reduceMotion,
  onExit,
  onOpenRoom,
}: {
  roomId: string;
  settings: Settings;
  reduceMotion: boolean;
  onExit: () => void;
  onOpenRoom: (id: string) => void;
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [identity, setIdentity] = useState<OnlineIdentity>(loadIdentity);

  useEffect(() => {
    let live = true;
    void fetchRoom(roomId).then(async (room) => {
      if (!live) return;
      if (room === 'unavailable') return setStage({ kind: 'unavailable' });
      if (!room) return setStage({ kind: 'missing' });
      // A finished game opens live when this browser may have played it (it has an online name):
      // the room tells its players how it ended and any rematch offered while they were away,
      // and anyone else watches its replay (LiveRoom). Shared replays are only replays.
      const ended = room.status === 'finished';
      if (room.kind === 'replay' || (ended && !identity.name)) {
        const found = await fetchRoomReplay(roomId);
        if (!live) return;
        return setStage(found ? { kind: 'replay', ...found } : { kind: 'missing' });
      }
      setStage(identity.name ? { kind: 'live', ended } : { kind: 'name' });
    });
    return () => {
      live = false;
    };
    // The identity only matters for the first decision; a new name is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  switch (stage.kind) {
    case 'loading':
      return <Message text={t('room.connecting')} onExit={onExit} />;
    case 'missing':
      return <Message text={t('room.notFound')} onExit={onExit} error />;
    case 'unavailable':
      return <Message text={t('room.unavailable')} onExit={onExit} error />;
    case 'replay':
      return (
        <ReplayViewer
          replay={stage.replay}
          names={seatNames(stage.room)}
          settings={settings}
          reduceMotion={reduceMotion}
          onBack={onExit}
        />
      );
    case 'name':
      return (
        <NameForm
          refused={stage.refused}
          onExit={onExit}
          onDone={(next) => {
            setIdentity(next);
            setStage({ kind: 'live' });
          }}
        />
      );
    case 'live':
      return (
        <LiveRoom
          key={roomId}
          roomId={roomId}
          ended={stage.ended ?? false}
          identity={identity}
          settings={settings}
          reduceMotion={reduceMotion}
          onExit={onExit}
          onOpenRoom={onOpenRoom}
          onNameRefused={(refused) => setStage({ kind: 'name', refused })}
        />
      );
  }
}

function Message({ text, onExit, error }: { text: string; onExit: () => void; error?: boolean }) {
  return (
    <main className="page page-room">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onExit}>
          ← {t('menu.back')}
        </button>
        <h1>{t('online.title')}</h1>
      </header>
      <p className={error ? 'notice notice-error' : 'dim'} role={error ? 'alert' : 'status'}>
        {text}
      </p>
    </main>
  );
}

function NameForm({
  refused,
  onExit,
  onDone,
}: {
  refused?: NameRefusal;
  onExit: () => void;
  onDone: (identity: OnlineIdentity) => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(() =>
    // A name the room refused although the game allows it: a word the site's operator blocked.
    refused ? nameErrorText(refused === 'name-taken' ? 'taken' : 'offensive') : null,
  );
  const [notSaved, setNotSaved] = useState(identityNotSaved);
  const submit = () => {
    const saved = saveName(name);
    const unsaved = identityNotSaved();
    setNotSaved(unsaved);
    if ('error' in saved) setError(nameErrorText(saved.error));
    // Storage refused the identity just now: the player sees why first (pressing again joins).
    else if (!unsaved || notSaved !== null) onDone(saved.identity);
  };
  return (
    <main className="page page-room">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onExit}>
          ← {t('menu.back')}
        </button>
        <h1>{t('room.nameFirst')}</h1>
      </header>
      <section className="panel online-panel">
        <Field label={t('online.name')} hint={t('online.nameHint')}>
          <input
            type="text"
            value={name}
            maxLength={24}
            autoComplete="nickname"
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </Field>
        {error && (
          <p className="notice notice-error" role="alert">
            {error}
          </p>
        )}
        {notSaved && (
          <p className="notice notice-error" role="alert">
            {t('online.identityNotSaved', { message: notSaved })}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={submit}>
            {t('room.join')}
          </button>
        </div>
      </section>
    </main>
  );
}

/** Seconds left before an absent opponent's game may be claimed, updated every second. */
function useClaimCountdown(since: string | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);
  if (!since) return null;
  return Math.max(0, Math.ceil((Date.parse(since) + ABANDON_MS - now) / 1000));
}

function LiveRoom({
  roomId,
  ended,
  identity,
  settings,
  reduceMotion,
  onExit,
  onOpenRoom,
  onNameRefused,
}: {
  roomId: string;
  /** The game was over when the room was opened. */
  ended: boolean;
  identity: OnlineIdentity;
  settings: Settings;
  reduceMotion: boolean;
  onExit: () => void;
  onOpenRoom: (id: string) => void;
  /** The room did not take the player's name: another must be chosen. */
  onNameRefused: (refused: NameRefusal) => void;
}) {
  const host = useMemo(() => new RemoteHost(roomId, identity), [roomId, identity]);
  useEffect(() => {
    host.connect();
    return () => host.close();
  }, [host]);
  const room = useSyncExternalStore(host.subscribe, host.getRoom);
  const [askedRematch, setAskedRematch] = useState(false);
  useEffect(() => {
    if (room.nameRefused) onNameRefused(room.nameRefused);
  }, [room.nameRefused, onNameRefused]);
  const info = room.room;
  const me: Side | null = room.role === 'S' || room.role === 'N' ? room.role : null;
  const them: Side | null = me ? opponent(me) : null;
  const countdown = useClaimCountdown(them && info ? info.away[them] : undefined);

  // A rematch I asked for opens as soon as the room exists; my opponent's is offered.
  useEffect(() => {
    if (room.rematch && askedRematch) onOpenRoom(room.rematch.id);
  }, [room.rematch, askedRematch, onOpenRoom]);

  const link = roomLink(roomId);
  const names = seatNames(info);

  // A game that was over when opened: its replay comes over HTTP as well, in case the room
  // cannot be reached live (WebSockets blocked, say). It is shown once the room has not come
  // alive for a while, or at once when the player asks.
  const [fetched, setFetched] = useState<{ replay: Replay; room: RoomInfo } | null>(null);
  const [giveUpLive, setGiveUpLive] = useState(false);
  useEffect(() => {
    if (!ended) return;
    let live = true;
    void fetchRoomReplay(roomId).then((found) => {
      if (live) setFetched(found);
    });
    const timer = setTimeout(() => setGiveUpLive(true), LIVE_WAIT_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [ended, roomId]);
  const fallback = ended && giveUpLive && !room.hasView ? fetched : null;

  // A game that was over when opened, by someone who did not play it (or whose room cannot be
  // reached): its replay, and no need for the connection any more.
  const replayOnly =
    ended && room.role === 'spectator' && room.replay
      ? { replay: room.replay, names }
      : fallback
        ? { replay: fallback.replay, names: seatNames(fallback.room) }
        : null;
  const showReplay = replayOnly !== null;
  useEffect(() => {
    if (showReplay) host.close();
  }, [showReplay, host]);
  if (replayOnly)
    return (
      <ReplayViewer
        replay={replayOnly.replay}
        names={replayOnly.names}
        settings={settings}
        reduceMotion={reduceMotion}
        onBack={onExit}
      />
    );

  if (!info || !room.hasView) {
    const waiting = info?.status === 'waiting' && me !== null;
    // A rematch: both seats are taken, and it waits for the other player to come back.
    const rematchOf = waiting && them && info.seats[them] ? names[them] : null;
    return (
      <main className="page page-room">
        <header className="page-head">
          <button type="button" className="btn btn-small" onClick={onExit}>
            ← {t('menu.back')}
          </button>
          <h1>{waiting ? t('room.waitingTitle') : t('online.title')}</h1>
        </header>
        {waiting ? (
          <section className="panel online-panel">
            {rematchOf ? (
              <p>{t('room.waitingRematch', { name: rematchOf })}</p>
            ) : (
              <>
                <p>{t('room.waitingBody')}</p>
                <ShareLink url={link} title={t('app.title')} />
              </>
            )}
            <p className="dim waiting-dots" role="status">
              {t('room.waitingTitle')}
              <span className="dots" aria-hidden="true" />
            </p>
            <button type="button" className="btn" onClick={onExit}>
              {t('room.leave')}
            </button>
          </section>
        ) : (
          <>
            <p className="dim" role="status">
              {room.connection === 'reconnecting' ? t('room.reconnecting') : t('room.connecting')}
            </p>
            {ended && fetched && (
              <button type="button" className="btn" onClick={() => setGiveUpLive(true)}>
                {t('room.watchReplay')}
              </button>
            )}
          </>
        )}
        {room.error && (
          <p className="notice notice-error" role="alert">
            {room.error}
          </p>
        )}
      </main>
    );
  }

  const theirName = them ? names[them] : '';
  const banner = (
    <>
      {room.connection !== 'open' && (
        <p className="notice online-banner" role="status">
          {t('room.reconnecting')}
        </p>
      )}
      {room.role === 'spectator' && (
        <p className="notice online-banner" role="status">
          {t('room.spectating')}
        </p>
      )}
      {them && info.status === 'playing' && info.away[them] && (
        <p className="notice online-banner" role="status">
          {countdown && countdown > 0
            ? t('room.awayWait', { name: theirName, seconds: countdown })
            : t('room.away', { name: theirName })}
          {countdown === 0 && (
            <button type="button" className="btn btn-small" onClick={() => host.claim()}>
              {t('room.claim')}
            </button>
          )}
        </p>
      )}
      {room.rematch && !askedRematch && me && (
        <p className="notice online-banner" role="status">
          {room.rematch.by === me
            ? t('room.rematchReady')
            : t('room.rematchOffered', { name: names[room.rematch.by] })}
          <button
            type="button"
            className="btn btn-small btn-primary"
            onClick={() => onOpenRoom(room.rematch!.id)}
          >
            {t('room.rematchJoin')}
          </button>
        </p>
      )}
    </>
  );

  const change = me ? info.result?.ratings?.[me] : undefined;
  const endExtra = change ? (
    <p className="rating-change">
      {t('room.rating', {
        before: change.before,
        after: change.after,
        delta: `${change.after >= change.before ? '+' : ''}${change.after - change.before}`,
      })}
    </p>
  ) : null;

  return (
    <MatchScreen
      host={host}
      settings={settings}
      reduceMotion={reduceMotion}
      title={t('room.title', { a: names.S, b: names.N })}
      exitLabel={t('menu.back')}
      onExit={onExit}
      canConcede={me !== null}
      banner={banner}
      endExtra={endExtra}
      onShare={() => Promise.resolve({ url: link })}
      onRematch={
        me && info.status === 'finished'
          ? () => {
              setAskedRematch(true);
              if (room.rematch) onOpenRoom(room.rematch.id);
              else host.askRematch();
            }
          : undefined
      }
      onSaveReplay={
        room.replay
          ? () => {
              const { error } = addReplay({
                title: `${names.S} vs ${names.N}`,
                result: host.getSnapshot().view.result,
                names,
                replay: room.replay!,
              });
              return error ? t('error.storage', { message: error }) : null;
            }
          : undefined
      }
      onExport={
        room.replay ? () => downloadJson(`pawned-online-${roomId}.json`, room.replay) : undefined
      }
    />
  );
}
